import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import validate from 'validate.js'
import useChangeEvent from '@hooks/processor/useChangeEvent'
import { rootActions } from '@app-store/slices'
import { RootState } from '@app-store/store'
import { history } from '@components/App'
import routes from '@utils/constants/routes'
import { COOKIE_KEYS, getCookie } from '@features/General'
import { USER_ROLES } from '@utils/constants'
import { getSubDomain } from '@features/Auth'
import { androidLinks, iosLinks } from 'config'
import { emailRule } from '@features/General/constants/formRules'
import isEmpty from 'lodash/isEmpty'
import { setEventCookie, setUserAuthCookie, setRefreshTokenCookie } from '@features/Auth'
import useMixpanel from 'hooks/processor/useMixpanel'

function useAuth() {
    const dispatch = useDispatch()
    const subdomain = getSubDomain()
    const { trackData } = useMixpanel()
    const [errors, setErrors] = useState<any>({})
    const [errorLink, setErrorLink] = useState<'sign-up' | 'sign-in' | 'confirmation'>(null)
    const { callChangeEventEndpoints } = useChangeEvent()
    const { data: activationData } = useSelector((state: RootState) => state.users.activation)
    const { redirectRoute } = useSelector((state: RootState) => state.ui.authSlider)
    const { data: eventByDomainData } = useSelector((state: RootState) => state.events.oneEventByDomain)
    const { data: findUserData } = useSelector((state: RootState) => state.users.findUser)

    const checkExistenceEmailError = (data, isLogin = true) => {
        dispatch(rootActions.ui.authSlider.setLoggedInInfo({ loggingIn: false }))
        if (data?.user && !isLogin) {
            setErrors({
                ...errors,
                email: `There is an account connected to this email. Use a new email, or click ##link## to signin`
            })
            setErrorLink('sign-in')
        } else if (data?.user && data?.userStatus === 'UNCONFIRMED') {
            setErrors({
                ...errors,
                email: `${
                    isLogin ? 'Your account' : 'This email exists in the system and'
                } has not been confirmed. Please click ##link## to confirmation.`
            })
            setErrorLink('confirmation')
        } else if (!isLogin) {
            setErrors({
                ...errors,
                email: null
            })
        } else if (data?.contact === false && !data?.user) {
            setErrors({
                ...errors,
                email: 'The email you entered isn’t connected to an account. Use the correct email, or click ##link## to signup'
            })
            setErrorLink('sign-up')
        } else if (data?.contact && !data?.user && eventByDomainData?.isPublicEvent !== false) {
            if (isLogin) {
                history.push({
                    pathname: routes.sign_up._,
                    search: '?activation'
                })
            }
        } else if (data?.dbUser === false) {
            if (eventByDomainData?.isPublicEvent) {
                setErrors({
                    ...errors,
                    email: 'This event is close for public registration, please contact the oganzier'
                })
            } else {
                setErrors({
                    ...errors,
                    email: 'The user doesn’t exist. Use the correct email, or click ##link## to signup'
                })
                setErrorLink('sign-up')
            }
        } else {
            setErrors({
                ...errors,
                email: null
            })
        }
    }

    const checkExistenceUser = (email, isLogin = true) => {
        const errorsForm = validate({ email }, emailRule)
        if (email && !errorsForm?.email && email !== findUserData?.email) {
            setErrors({
                ...errors,
                email: null
            })
            const payload: {
                email: string
                eventId?: string
            } = { email }
            if (eventByDomainData?._id) {
                payload.eventId = eventByDomainData._id
            }
            dispatch(
                rootActions.users.findUser.onRequest({
                    data: payload,
                    sagaCB: {
                        onSuccess: response => {
                            checkExistenceEmailError(response, isLogin)
                        }
                    }
                })
            )
        }
    }

    const resendConfirmationCode = email => {
        const values = {
            username: email,
            sagaCB: {
                onSuccess: () => {
                    history.push(routes.login.confirmation._)
                }
            }
        }
        dispatch(rootActions.auth.resendConfirmationCode.onRequest(values))
    }

    const successLogin = (response, isLoginModal = false, isPaymentLogin = false, onSuccess = undefined) => {
        const { jwtToken } = response?.idToken
        const { token } = response?.refreshToken
        // Some browsers like Safari does not allow set coockies for http protocol
        // So will disable this flag for localhost just for development
        const cookiePath =
            window.location.href.includes('localhost:7080') && process.env.NODE_ENV === 'development' ? '' : 'secure;'

        if (!isEmpty(token)) setRefreshTokenCookie(token, cookiePath)

        if (!isEmpty(jwtToken)) {
            setUserAuthCookie(jwtToken, cookiePath)
            if (eventByDomainData?._id) {
                setEventCookie(eventByDomainData._id)
                // Set this flag as true to will not call endpoints in callChangeEventEndpoints in Provider
                dispatch(rootActions.ui.authSlider.setLoggedInInfo({ eventChanged: true }))
            }
            dispatch(rootActions.auth.login.onSuccess({ token: jwtToken }))
            dispatch(rootActions.ui.authSlider.setLoggedInInfo({ loginModalVisible: false }))
            const callEndpoints = () => {
                const preLoginEventId = activationData?.eventId || eventByDomainData?._id
                if (redirectRoute && preLoginEventId) {
                    callChangeEventEndpoints({
                        eventId: preLoginEventId,
                        redirectRoute,
                        isPaymentLogin,
                        onSuccess
                    })
                    dispatch(rootActions.ui.authSlider.setLoggedInInfo({ redirectRoute: '' }))
                } else if (!isLoginModal) {
                    if (preLoginEventId) {
                        const isViewer = getCookie(COOKIE_KEYS.ALA_USER_ROLE) === USER_ROLES.VIEWER
                        callChangeEventEndpoints({
                            eventId: preLoginEventId,
                            redirectRoute: redirectRoute || (isViewer ? routes.analytics._ : ''),
                            isPaymentLogin,
                            onSuccess
                        })
                    } else {
                        dispatch(rootActions.ui.authSlider.setLoggedInInfo({ loggingIn: false }))
                        dispatch(rootActions.users.profile.onRequest({}))
                        if (!isPaymentLogin) {
                            history.replace(routes.organizer.events._)
                        }
                        if (typeof onSuccess === 'function') {
                            onSuccess()
                        }
                    }
                } else if (isLoginModal) {
                    dispatch(rootActions.ui.authSlider.setLoggedInInfo({ loggingIn: false }))
                    location.reload()
                }
            }
            if ((typeof onSuccess === 'function' && isPaymentLogin) || typeof onSuccess !== 'function') {
                callEndpoints()
            }
        }
    }

    const onLogin = async (
        values,
        isActivation = false,
        isLoginModal = false,
        loginType = '',
        isPaymentLogin = false,
        onSuccess = undefined
    ) => {
        dispatch(rootActions.ui.authSlider.setLoggedInInfo({ loggingIn: true }))
        values = {
            ...values,
            isActivation,
            sagaCB: {
                onSuccess: async response => {
                    if (loginType) {
                        trackData('Login', {
                            'Login Type': loginType
                        })
                    }
                    successLogin(response, isLoginModal, isPaymentLogin, onSuccess)
                }
            }
        }
        dispatch(rootActions.auth[values?.code ? 'loginWithOtp' : 'login'].onRequest(values))
    }
    const onSuccessForNonDesktopDevice = elseFunction => {
        if (navigator.userAgent.match(/Tablet|iPad|iPhone|iPod/i)) {
            window.location.replace(iosLinks[subdomain] || iosLinks.main)
        } else if (
            navigator.userAgent.match(
                /Mobile|Windows Phone|Lumia|Android|webOS|Blackberry|PlayBook|BB10|Opera Mini|\bCrMo\/|Opera Mobi/i
            )
        ) {
            window.location.replace(androidLinks[subdomain] || androidLinks.main)
        } else {
            elseFunction()
        }
    }
    function validationForm(values, constraints) {
        const errorsForm = validate(values, constraints)
        if (errorsForm) {
            setErrors(errorsForm)
        } else {
            setErrors({})
        }
        return errorsForm
    }
    return {
        onLogin,
        errors,
        setErrors,
        errorLink,
        checkExistenceUser,
        resendConfirmationCode,
        checkExistenceEmailError,
        validationForm,
        onSuccessForNonDesktopDevice,
        successLogin
    }
}

export default useAuth
